import { gql } from '@apollo/client'
import { SortingState } from '@tanstack/react-table'

import { apolloClient } from './apolloClient'
import { ConnectionResult } from './listQuery'

export const adminOrganizationsQuery = gql`
  query AdminOrganizations(
    $first: Int
    $skip: Int
    $filter: HeloOrganizationFilterInput
    $orderBy: [HeloOrganizationOrderByInput!]
  ) {
    Organization_list(
      first: $first
      skip: $skip
      filter: $filter
      orderBy: $orderBy
    ) {
      totalCount
      hasNextPage
      hasPreviousPage
      items {
        id
        name
        displayName
        auth0Id
        createdDate
        updatedDate
      }
    }
  }
`

export const organizationByIdQuery = gql`
  query OrganizationById($id: String!) {
    organizationById(id: $id) {
      id
      name
      displayName
      auth0Id
      createdDate
      updatedDate
    }
  }
`

export type AdminOrganization = {
  id: string
  name: string
  displayName: string
  auth0Id: string
  createdDate: string
  updatedDate: string
}

export type AdminOrganizationFilter = {
  name?: { contains?: string }
  displayName?: { contains?: string }
  auth0Id?: { contains?: string }
}

export type AdminOrganizationOrderByInput = {
  name?: 'ASC' | 'DESC'
  displayName?: 'ASC' | 'DESC'
  auth0Id?: 'ASC' | 'DESC'
  createdDate?: 'ASC' | 'DESC'
  updatedDate?: 'ASC' | 'DESC'
}

export type AdminOrganizationsQueryOptions = {
  first?: number
  skip?: number
  filter?: AdminOrganizationFilter
  orderBy?: AdminOrganizationOrderByInput[]
}

export const mapOrganizationsSortingToOrderBy = (
  sorting: SortingState,
): AdminOrganizationOrderByInput[] | undefined => {
  const orderBy: AdminOrganizationOrderByInput[] = []

  sorting.forEach(sort => {
    const direction = sort.desc ? 'DESC' : 'ASC'

    if (sort.id === 'name') orderBy.push({ name: direction })
    if (sort.id === 'displayName') orderBy.push({ displayName: direction })
    if (sort.id === 'auth0Id') orderBy.push({ auth0Id: direction })
    if (sort.id === 'createdDate') orderBy.push({ createdDate: direction })
    if (sort.id === 'updatedDate') orderBy.push({ updatedDate: direction })
  })

  return orderBy.length > 0 ? orderBy : undefined
}

export const getOrganizations = async (
  options?: AdminOrganizationsQueryOptions,
): Promise<ConnectionResult<AdminOrganization>> => {
  const result = await apolloClient.query({
    query: adminOrganizationsQuery,
    variables: {
      first: options?.first ?? 50,
      skip: options?.skip ?? 0,
      filter: options?.filter,
      orderBy: options?.orderBy,
    },
  })
  return ((result.data as any)?.Organization_list ?? {
    items: [],
  }) as ConnectionResult<AdminOrganization>
}

export const getOrganizationById = async (
  id: string,
): Promise<AdminOrganization | null> => {
  const result = await apolloClient.query({
    query: organizationByIdQuery,
    variables: { id },
  })
  return ((result.data as any)?.organizationById ??
    null) as AdminOrganization | null
}
